import { fetchPhysicianByNpi } from '@/entities/physician';
import {
  checkNull,
  checkNullId,
  checkToFormatPhoneNumber,
  handleFetchDivisions,
  isWebViewCreateStudy,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import type {
  CreateReferringPhysicianDrawerProps,
  CreateReferringPhysicianDrawerState,
  KeyDownEvent,
  ScrollEvent,
} from './createReferringPhysician.types';
import { DEFAULT_PHYSICIAN_INFO, idArr } from '../lib/createReferringPhysicianDrawer';

/**
 * Drives the drawer for adding a referring physician, with an NPI lookup tab and a manual entry
 * tab. Keeps the form, errors, tab and search state, resets them each time the drawer opens,
 * focuses the NPI input and binds Enter to the search on the lookup tab, and prefills the facility
 * address when switching to manual entry.
 *
 * @param handleClickClose - Closes the drawer.
 * @param data - Study data whose facility contact prefills the manual entry form.
 * @param handleClickCreate - Receives the validated physician info on submit.
 * @param countries - Country options for the country select.
 * @param visible - Whether the drawer is open.
 * @returns An object carrying the drawer state, the NPI input ref, display flags and the form handlers.
 */
export const useCreateReferringPhysicianDrawer = ({
  handleClickClose,
  data,
  handleClickCreate,
  countries,
  visible,
}: Pick<
  CreateReferringPhysicianDrawerProps,
  'handleClickClose' | 'data' | 'handleClickCreate' | 'countries' | 'visible'
>) => {
  const [state, setState] = useMergeState<CreateReferringPhysicianDrawerState>({
    isScrolling: false,
    errorObject: {},
    activeTab: '1',
    searchedReferringPhysicianInfo: null,
    npi: '',
    isSearchingPhysician: false,
    notFoundPhysician: false,
    referringPhysicianInfo: { ...DEFAULT_PHYSICIAN_INFO },
    country: null,
  });

  const npiInputRef = useRef<HTMLInputElement>(null);

  const isWebView = isWebViewCreateStudy();

  const shouldFormatPhoneNumber = checkToFormatPhoneNumber(state.referringPhysicianInfo.country);

  /**
   * Moves focus to the next field of the physician form when Enter is pressed. Locates the current input in the ordered id list and focuses the following id when both pass the null check, deliberately leaving the default action intact so react-select can still accept an option.
   *
   * @param e - Key event observed on a form input.
   * @returns Nothing.
   */
  const handleKeyDownInput = (e: any) => {
    const idIndex = _.indexOf(idArr, e.target.id) !== -1 ? _.indexOf(idArr, e.target.id) : null;
    if (
      idIndex !== null &&
      e.key === 'Enter' &&
      checkNullId(idArr[idIndex]) &&
      checkNullId(idArr[idIndex + 1])
    ) {
      // *: Do not use preventDefault to allow react select input select option when pressing Enter
      // e.preventDefault();
      document.getElementById(idArr[idIndex + 1])?.focus();
    }
  };

  /**
   * Tracks whether the drawer body has scrolled away from the top so the header can react. Flips the isScrolling flag only when the scrollTop reading disagrees with the flag already held.
   *
   * @param e - Scroll event carrying the scrolled element.
   * @returns Nothing.
   */
  const handleScroll = (e: ScrollEvent) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && state.isScrolling) {
      setState({ isScrolling: false });
    } else if (scrollTop !== 0 && !state.isScrolling) {
      setState({ isScrolling: true });
    }
  };

  /**
   * Closes the create referring physician drawer. Delegates straight to the handleClickClose callback supplied by the parent.
   *
   * @returns Nothing.
   */
  const onClickClose = () => {
    handleClickClose();
  };

  /**
   * Prefills the referring physician form from the facility's own contact details when the drawer opens. Matches the facility country against the countries list, loads its divisions, copies address, city, zip, phone1, night phone and fax across, and resolves the state through the divisions for US and CA or takes it verbatim for other countries.
   *
   * @returns A promise resolving once the resolved defaults have been pushed into drawer state.
   */
  const createInitialData = async () => {
    const { facility } = data;
    /**
     * The country option matching the facility contact's country. Selected from the countries list with a lodash find over facility?.contact?.country.
     *
     * @returns The matching country option, or undefined when the facility country is absent from the list.
     */
    const defaultCountry = _.find(countries, (x) => x.value === facility?.contact?.country);
    if (defaultCountry) {
      const countryCode = defaultCountry.value;
      const divisions = await handleFetchDivisions(countryCode);

      const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };
      newReferringPhysicianInfo.country = countryCode;

      newReferringPhysicianInfo.address = facility?.contact?.address;
      newReferringPhysicianInfo.city = facility?.contact?.city;
      newReferringPhysicianInfo.zip = facility?.contact?.zip;
      newReferringPhysicianInfo.phone1 = facility?.contact?.phone1;
      newReferringPhysicianInfo.nightPhone = facility?.contact?.nightPhone;
      newReferringPhysicianInfo.fax = facility?.contact?.fax;

      let defaultState;
      if (countryCode === 'US' || countryCode === 'CA') {
        defaultState = _.find(divisions, (x) => x.value === facility?.contact?.state);
        if (defaultState) {
          newReferringPhysicianInfo.state = defaultState.value;
        }
      } else {
        defaultState = facility?.contact?.state;
        newReferringPhysicianInfo.state = defaultState;
      }

      setState({
        country: defaultCountry,
        divisions,
        referringPhysicianInfo: newReferringPhysicianInfo,
        state: defaultState,
      });
    }
  };

  /**
   * Records a plain text edit on the referring physician form. Copies the physician info object, assigns the named field and writes the copy back to drawer state.
   *
   * @param name - Field being edited.
   * @param value - New field value.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };
    newReferringPhysicianInfo[name] = value;
    setState({ referringPhysicianInfo: newReferringPhysicianInfo });
  };

  /**
   * Handles a dropdown selection on the referring physician form. Stores the chosen option under its field name, and for the country field loads divisions for US or CA, blanks address, city, state and zip and drops the selected state option, before writing the option value onto the form.
   *
   * @param name - Field the selection belongs to.
   * @param value - Raw selected value, unused in favour of the option.
   * @param option - Selected option carrying label and value.
   * @returns A promise resolving once the drawer state has been updated.
   */
  const onChangeSelection = async (name: any, value: any, option: any) => {
    const setStateObject = { [name]: option };

    const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };

    if (name === 'country') {
      if (option.value === 'US' || option.value === 'CA') {
        const countryCode = option.value;
        const divisions = await handleFetchDivisions(countryCode);
        _.assign(setStateObject, { option, divisions });
      }

      newReferringPhysicianInfo.address = '';
      newReferringPhysicianInfo.city = '';
      newReferringPhysicianInfo.state = '';
      newReferringPhysicianInfo.zip = '';

      _.assign(setStateObject, { state: undefined });
    }

    newReferringPhysicianInfo[name] = option.value;
    _.assign(setStateObject, { referringPhysicianInfo: newReferringPhysicianInfo });

    setState(setStateObject);
  };

  /**
   * Validates the referring physician form and submits it once clean. Runs the required and optional field list through checkNull, adds an NPI message unless a supplied NPI has ten digits, and either stores the error object on state or hands the physician info to handleClickCreate.
   *
   * @returns Nothing.
   */
  const onClickCreate = () => {
    const checkNullData = [
      { field: 'firstName', value: state.referringPhysicianInfo.firstName, isRequired: true },
      { field: 'lastName', value: state.referringPhysicianInfo.lastName, isRequired: true },
      { field: 'title', value: state.referringPhysicianInfo.title, isRequired: true },
      { field: 'country', value: state.referringPhysicianInfo.country, isRequired: true },
      { field: 'address', value: state.referringPhysicianInfo.address, isRequired: true },
      { field: 'city', value: state.referringPhysicianInfo.city, isRequired: true },
      { field: 'state', value: state.referringPhysicianInfo.state, isRequired: true },
      { field: 'zip', value: state.referringPhysicianInfo.zip, isRequired: true },
      { field: 'phone1', value: state.referringPhysicianInfo.phone1, isRequired: true },
      { field: 'nightPhone', value: state.referringPhysicianInfo.nightPhone, isRequired: false },
      { field: 'email', value: state.referringPhysicianInfo.email, isRequired: false },
      { field: 'fax', value: state.referringPhysicianInfo.fax, isRequired: false },
    ];

    const errorObject = checkNull(checkNullData);
    if (state.referringPhysicianInfo?.npi && state.referringPhysicianInfo.npi.length !== 10) {
      _.assign(errorObject, { npi: 'Please enter 10-digit NPI number.' });
    }

    if (!_.isEmpty(errorObject)) {
      setState({ errorObject });
    } else {
      handleClickCreate(state.referringPhysicianInfo);
    }
  };

  /**
   * Keeps the physician form in step with free text typed into the address or city autocomplete. Writes the text onto city when the search runs by city and onto address otherwise.
   *
   * @param value - Text currently in the autocomplete input.
   * @param isSearchCity - Whether the autocomplete searches by city rather than street address.
   * @returns Nothing.
   */
  const onChangeInputAutoComplete = (value: string, isSearchCity: boolean | undefined) => {
    const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };
    if (!isSearchCity) {
      newReferringPhysicianInfo.address = value;
    } else {
      newReferringPhysicianInfo.city = value;
    }
    setState({ referringPhysicianInfo: newReferringPhysicianInfo });
  };

  /**
   * Applies a picked address suggestion to the referring physician form. Copies city, state name and zip across plus the street address unless the search ran by city, and when the suggestion carries a country reloads the divisions on a country change and keeps a state option only for US and CA.
   *
   * @param data - Address suggestion chosen from the autocomplete.
   * @param isSearchCity - Whether the autocomplete searched by city rather than street address.
   * @returns A promise resolving once the drawer state has been updated.
   */
  const onChangeAutoComplete = async (data: any, isSearchCity: boolean | undefined) => {
    const {
      country: autoCompleteCountry,
      address: autoCompleteAddress,
      city: autoCompleteCity,
      state: autoCompleteState,
      zip: autoCompleteZip,
    } = data;

    const setStateObject = {};

    const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };
    if (!isSearchCity) {
      newReferringPhysicianInfo.address = autoCompleteAddress;
    }
    newReferringPhysicianInfo.city = autoCompleteCity;
    newReferringPhysicianInfo.state = autoCompleteState.name;
    newReferringPhysicianInfo.zip = autoCompleteZip;

    if (autoCompleteCountry) {
      const autoCompleteCountryCode = autoCompleteCountry?.code;

      if (autoCompleteCountryCode) {
        if (autoCompleteCountryCode !== newReferringPhysicianInfo.country) {
          newReferringPhysicianInfo.country = autoCompleteCountryCode;
          const divisions = await handleFetchDivisions(autoCompleteCountryCode);
          _.assign(setStateObject, { divisions });
        }

        if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
          _.assign(setStateObject, {
            state: {
              label: autoCompleteState.name,
              value: autoCompleteState.code,
            },
          });
        } else {
          _.assign(setStateObject, { state: undefined });
        }
      }
    }

    _.assign(setStateObject, { referringPhysicianInfo: newReferringPhysicianInfo });

    setState(setStateObject);
  };

  /**
   * Clears the address fields the place autocomplete filled into the referring physician form. Copies the physician info blanking city, state and zip, blanks the address too unless the search ran by city, and drops the selected state option.
   *
   * @param isSearchCity - Whether the autocomplete searched by city rather than street address.
   * @returns Nothing.
   */
  const clearAutoComplete = (isSearchCity: boolean | undefined) => {
    const newReferringPhysicianInfo = { ...state.referringPhysicianInfo };

    newReferringPhysicianInfo.city = '';
    newReferringPhysicianInfo.state = '';
    newReferringPhysicianInfo.zip = '';
    if (!isSearchCity) {
      newReferringPhysicianInfo.address = '';
    }

    setState({
      state: undefined,
      referringPhysicianInfo: newReferringPhysicianInfo,
    });
  };

  /**
   * Tracks the NPI number typed into the physician lookup field. Stores only the value on drawer state and ignores the field name argument.
   *
   * @param name - Name of the changed field, unused.
   * @param value - NPI text entered by the user.
   * @returns Nothing.
   */
  const handleNpiChange = (name: string, value: string) => {
    setState({ npi: value });
  };

  /**
   * Looks a referring physician up by the entered NPI number and prefills the form from the registry result. Rejects an NPI that is not ten digits and flags not-found when the lookup returns nothing, then maps the mailing address onto the form, loading divisions and resolving the state option for US and CA, falling back to US when the returned country is unknown, and marking not-found on a logged error.
   *
   * @returns A promise resolving once the search outcome has been written into drawer state.
   */
  const handleSearchClick = async () => {
    if (state.npi?.length !== 10) {
      setState({
        errorObject: {
          npi: 'Please enter 10-digit NPI number.',
        },
        searchedReferringPhysicianInfo: null,
        notFoundPhysician: false,
      });
      return;
    }

    try {
      setState({ isSearchingPhysician: true });

      const physicianByNpi = (await fetchPhysicianByNpi(state.npi)) as any;
      const { results } = physicianByNpi;
      const result = results[0];
      if (!result) {
        setState({
          searchedReferringPhysicianInfo: null,
          referringPhysicianInfo: { ...DEFAULT_PHYSICIAN_INFO },
          isSearchingPhysician: false,
          notFoundPhysician: true,
        });
        return;
      }

      const setStateObject = {
        notFoundPhysician: false,
      };

      /**
       * The mailing address entry of the physician returned by the NPI lookup. Selected from result.addresses by matching address_purpose against MAILING.
       *
       * @returns The mailing address record of the physician.
       */
      const mailingAddress = result.addresses.find((x: any) => x.address_purpose === 'MAILING');

      const searchedReferringPhysicianInfo: any = {
        firstName: result.basic.first_name,
        lastName: result.basic.last_name,
        title: result.basic.credential,
        npi: result.number,
        country: undefined,
        address: undefined,
        city: undefined,
        state: undefined,
        zip: undefined,
        phone1: mailingAddress.telephone_number,
        nightPhone: undefined,
        email: undefined,
        fax: undefined,
      };

      const physicianCountry = mailingAddress.country_code;
      /**
       * The country option matching the country code on the physician's mailing address. Found in countries by comparing each option value with physicianCountry, and its absence stops the address fields from being prefilled.
       *
       * @returns The matching country option, or undefined when the code is unknown.
       */
      const physicianCountryOption = countries?.find((x) => x.value === physicianCountry);
      const shouldPrefillAddressInfo = !!physicianCountryOption;
      if (shouldPrefillAddressInfo) {
        Object.assign(setStateObject, {
          country: physicianCountryOption,
        });

        if (physicianCountry === 'US' || physicianCountry === 'CA') {
          const divisions = await handleFetchDivisions(physicianCountry);
          Object.assign(setStateObject, { divisions });

          const physicianState = mailingAddress.state;
          /**
           * The division option matching the searched physician's mailing state. Found in the divisions loaded for the physician country by comparing each option value with physicianState.
           *
           * @returns The matching state option, or undefined when no division matches.
           */
          const defaultState = divisions.find((x) => x.value === physicianState);
          if (defaultState) {
            Object.assign(setStateObject, { state: defaultState });
          }
        }

        Object.assign(searchedReferringPhysicianInfo, {
          country: physicianCountry,
          address: mailingAddress.address_1,
          city: mailingAddress.city,
          state: mailingAddress.state,
          zip: mailingAddress.postal_code,
        });
      } else {
        /**
         * The United States country option used as fallback when the searched physician's country is not among the known options. Located with a lodash find over countries for value US.
         *
         * @returns The US country option, or undefined when it is not in the list.
         */
        const defaultCountry = _.find(countries, (x) => x.value === 'US');
        if (defaultCountry) {
          Object.assign(searchedReferringPhysicianInfo, {
            country: defaultCountry.value,
          });

          const countryCode = defaultCountry.value;
          const divisions = await handleFetchDivisions(countryCode);

          Object.assign(setStateObject, {
            country: defaultCountry,
            divisions,
          });
        }
      }

      Object.assign(setStateObject, {
        searchedReferringPhysicianInfo,
        referringPhysicianInfo: {
          ...state.referringPhysicianInfo,
          ...searchedReferringPhysicianInfo,
        },
      });

      Object.assign(setStateObject, { isSearchingPhysician: false });

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to search referring physician: ', error);
      setState({
        isSearchingPhysician: false,
        notFoundPhysician: true,
      });
    }
  };

  /**
   * Switches the drawer between its NPI lookup and manual entry tabs. Does nothing when the tab is already active, otherwise storing the new tab and clearing the errors, the searched physician, the NPI input, both search flags, the physician form and the country and state selections.
   *
   * @param tab - Identifier of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: string) => {
    if (tab !== state.activeTab) {
      setState({
        activeTab: tab,
        errorObject: {},
        searchedReferringPhysicianInfo: null,
        npi: '',
        isSearchingPhysician: false,
        notFoundPhysician: false,
        referringPhysicianInfo: { ...DEFAULT_PHYSICIAN_INFO },
        country: null,
        state: null,
      });
    }
  };

  /**
   * Lets the Enter key trigger the NPI physician search from the drawer. Prevents the default action on keyCode 13 and calls handleSearchClick.
   *
   * @param event - Key event observed on the drawer.
   * @returns Nothing.
   */
  const handleKeyDownEventListener = (event: KeyDownEvent): void => {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleSearchClick();
    }
  };

  useEffect(() => {
    if (visible) {
      if (state.activeTab === '1') {
        if (npiInputRef.current) {
          npiInputRef.current.focus();
        }
      }
    }
  }, [visible, state.activeTab]);

  useEffect(() => {
    if (!visible || state.activeTab !== '1') {
      return undefined;
    }

    window.addEventListener('keydown', handleKeyDownEventListener);
    return () => {
      window.removeEventListener('keydown', handleKeyDownEventListener);
    };
  }, [visible, state.activeTab, state.npi]);

  useEffect(() => {
    if (visible) {
      setState({
        errorObject: {},
        activeTab: '1',
        searchedReferringPhysicianInfo: null,
        npi: '',
        isSearchingPhysician: false,
        notFoundPhysician: false,
        referringPhysicianInfo: { ...DEFAULT_PHYSICIAN_INFO },
      });
    }
  }, [visible]);

  // *: Clear errorObject when changing input fields
  useUpdateEffect(() => {
    if (!_.isEmpty(state.errorObject)) {
      setState({ errorObject: {} });
    }
  }, [state.referringPhysicianInfo]);

  useUpdateEffect(() => {
    if (state.errorObject.npi) {
      setState({
        errorObject: {
          ...state.errorObject,
          npi: undefined,
        },
      });
    }
  }, [state.npi]);

  useUpdateEffect(() => {
    if (state.activeTab === '2') {
      createInitialData();
    }
  }, [state.activeTab]);

  return {
    state,
    npiInputRef,
    isWebView,
    shouldFormatPhoneNumber,
    handleKeyDownInput,
    handleScroll,
    onClickClose,
    onChange,
    onChangeSelection,
    onClickCreate,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    handleNpiChange,
    handleSearchClick,
    navigateTab,
  };
};
